import { useState } from 'react';
import { useToast } from '../components/Toast';
import { CheckIcon, UserIcon, ClipboardDocumentIcon, UserGroupIcon, CalendarIcon } from '@heroicons/react/24/solid';
import { submitProspectApplication } from '../lib/data';

const onboardingStages = [
  {
    id: 1,
    content: 'Named for Consideration',
    icon: UserIcon,
    iconBackground: 'bg-gray-400',
  },
  {
    id: 2,
    content: 'Selected to Apply',
    icon: ClipboardDocumentIcon,
    iconBackground: 'bg-green-500',
  },
  {
    id: 3,
    content: 'Completed Application Form',
    icon: CheckIcon,
    iconBackground: 'bg-green-500',
  },
  {
    id: 4,
    content: 'Approved for Interview',
    icon: UserGroupIcon,
    iconBackground: 'bg-blue-500',
  },
  {
    id: 5,
    content: 'Invite to Lunch',
    icon: CalendarIcon,
    iconBackground: 'bg-blue-500',
  },
  {
    id: 6,
    content: 'Submitted Initial Dues',  
    icon: CalendarIcon,
    iconBackground: 'bg-purple-500',
  },
  {
    id: 7,
    content: 'Promoted to Member',  
    icon: CalendarIcon,
    iconBackground: 'bg-purple-500',
  }
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Prospect() {
  const { showToast } = useToast();
  const [currentStage, setCurrentStage] = useState(2); // Example: Selected to Apply stage
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    roleTitle: '',
    businessName: '',
    businessDescription: '',
    yearsInBusiness: '',
    organizationMemberships: '',
    referralType: '',
    attestNoLeadsGroup: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const missingRequired =
      !formData.fullName?.trim() ||
      !formData.email?.trim() ||
      !formData.phone?.trim() ||
      !formData.address?.trim() ||
      !formData.roleTitle?.trim() ||
      !formData.businessName?.trim() ||
      !formData.businessDescription?.trim() ||
      !formData.yearsInBusiness?.toString().trim() ||
      !formData.organizationMemberships?.trim() ||
      !formData.referralType?.trim() ||
      !formData.attestNoLeadsGroup;

    if (missingRequired) {
      const msg = 'Required fields are missing. Please fill out the form.';
      showToast(msg, { kind: 'error' });
      setIsSubmitting(false);
      return;
    }

    try {
      await submitProspectApplication(formData);
      // Update the current stage to reflect completion
      setCurrentStage(3); // Completed Application Form stage
      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        address: '',
        roleTitle: '',
        businessName: '',
        businessDescription: '',
        yearsInBusiness: '',
        organizationMemberships: '',
        referralType: '',
        attestNoLeadsGroup: false
      });
    } catch (err) {
      const msg = err.message || 'Failed to submit application. Please try again.';
      showToast(msg, { kind: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-playfair font-semibold text-gray-900 mb-8">
          NBNA Membership Application
        </h1>

        {/* Onboarding Progress */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <h2 className="text-xl font-playfair text-[rgb(14,64,105)] mb-6">
            Your Application Progress
          </h2>
          <div className="flow-root">
            <ul role="list" className="-mb-8">
              {onboardingStages.map((stage, stageIdx) => (
                <li key={stage.id}>
                  <div className="relative pb-8">
                    {stageIdx !== onboardingStages.length - 1 ? (
                      <span
                        className={classNames(
                          "absolute left-4 top-4 -ml-px h-full w-0.5",
                          stage.id <= currentStage ? "bg-blue-500" : "bg-gray-200"
                        )}
                        aria-hidden="true"
                      />
                    ) : null}
                    <div className="relative flex space-x-3">
                      <div>
                        <span
                          className={classNames(
                            stage.id <= currentStage ? stage.iconBackground : 'bg-gray-200',
                            'flex h-8 w-8 items-center justify-center rounded-full ring-8 ring-white'
                          )}
                        >
                          <stage.icon className="h-5 w-5 text-white" aria-hidden="true" />
                        </span>
                      </div>
                      <div className="flex min-w-0 flex-1 justify-between pt-1.5">
                        <div>
                          <p className={classNames(
                            "text-sm font-medium",
                            stage.id <= currentStage ? "text-[rgb(14,64,105)]" : "text-gray-500"
                          )}>
                            {stage.content}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Application Form */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-playfair text-[rgb(14,64,105)] mb-6">
            Membership Application
          </h2>
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                Full Name
              </label>
              <input
                type="text"
                name="fullName"
                id="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                id="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                Address
              </label>
              <input
                type="text"
                name="address"
                id="address"
                value={formData.address}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">
                Business Name
              </label>
              <input
                type="text"
                name="businessName"
                id="businessName"
                value={formData.businessName}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label htmlFor="roleTitle" className="block text-sm font-medium text-gray-700">
                Role/Job Title
              </label>
              <input
                type="text"
                name="roleTitle"
                id="roleTitle"
                value={formData.roleTitle}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label htmlFor="businessDescription" className="block text-sm font-medium text-gray-700">
                Business Description
              </label>
              <textarea
                name="businessDescription"
                id="businessDescription"
                rows={4}
                value={formData.businessDescription}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Please describe your business (max 500 words)"
                required
              />
            </div>

            <div>
              <label htmlFor="yearsInBusiness" className="block text-sm font-medium text-gray-700">
                Number of Years in Business/with Employer
              </label>
              <input
                type="number"
                name="yearsInBusiness"
                id="yearsInBusiness"
                value={formData.yearsInBusiness}
                onChange={handleInputChange}
                min="0"
                className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label htmlFor="organizationMemberships" className="block text-sm font-medium text-gray-700">
                Membership in Business and Social Organizations
              </label>
              <textarea
                name="organizationMemberships"
                id="organizationMemberships"
                rows={3}
                value={formData.organizationMemberships}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="List your current memberships in business and social organizations"
                required
              />
            </div>


            <div>
              <label htmlFor="referralType" className="block text-sm font-medium text-gray-700">
                Ideal Referral Type
              </label>
              <textarea
                name="referralType"
                id="referralType"
                rows={2}
                value={formData.referralType}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Describe your ideal client or referral"
                required
              />
            </div>

            {/* Attestation Checkbox */}
            <div className="border-t pt-6">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="attestNoLeadsGroup"
                    name="attestNoLeadsGroup"
                    type="checkbox"
                    checked={formData.attestNoLeadsGroup}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-[rgb(14,64,105)] border-gray-300 rounded focus:ring-[rgb(14,64,105)]"
                    required
                  />
                </div>
                <div className="ml-3">
                  <label htmlFor="attestNoLeadsGroup" className="text-sm text-gray-700">
                    I attest that I am not a member of another leads generation group
                  </label>
                </div>
              </div>
            </div>

            

            <div>
              <button
                type="submit"
                formNoValidate
                disabled={isSubmitting}
                className={`inline-flex justify-center rounded-md border border-transparent bg-[rgb(14,64,105)] py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-[rgb(14,64,105)]/90 focus:outline-none focus:ring-2 focus:ring-[rgb(14,64,105)] focus:ring-offset-2 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}